import {
  ActionIcon,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheckupList, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { useDeleteEinrichtung, useEinrichtungList } from '@/api/useEinrichtung';
import { ROLES, useCanWrite, useHasRole } from '@/auth/useRequireRole';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { EinrichtungEditDialog } from '@/pages/einrichtung/EinrichtungEditDialog';
import { useAppStore } from '@/store/appStore';
import type { Einrichtung } from '@/api/types';

function describeError(err: unknown): string {
  const ax = err as AxiosError<{ detail?: string | { message?: string } }> | undefined;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail && detail.message) {
    return String(detail.message);
  }
  return ax?.message ?? '';
}

export function EinrichtungListPage() {
  const { t } = useTranslation(['einrichtung', 'common', 'errors']);
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const canRead = useHasRole([ROLES.ADMIN, ROLES.TRAEGER_ADMIN, ROLES.PLANER, ROLES.LESEN]);
  const traegerId = useAppStore((s) => s.activeTraegerId);
  const setEinrichtung = useAppStore((s) => s.setEinrichtung);

  const list = useEinrichtungList(traegerId);
  const deleteMutation = useDeleteEinrichtung();

  const [editTarget, setEditTarget] = useState<Einrichtung | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Einrichtung | null>(null);

  if (!canRead) {
    return (
      <Stack>
        <Title order={2}>{t('einrichtung:title')}</Title>
        <ErrorState
          title={t('errors:missingRole.title')}
          error={t('errors:missingRole.description')}
        />
      </Stack>
    );
  }

  if (!traegerId) {
    return (
      <Stack>
        <Title order={2}>{t('einrichtung:title')}</Title>
        <EmptyState description={t('einrichtung:list.noTraeger')} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('einrichtung:title')}</Title>
          <Text c="dimmed">{t('einrichtung:subtitle')}</Text>
        </div>
        {canWrite ? (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
            {t('einrichtung:actions.create')}
          </Button>
        ) : null}
      </Group>

      {list.isLoading ? <LoadingState /> : null}
      {list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
      {list.data && list.data.length === 0 ? (
        <EmptyState
          title={t('einrichtung:list.empty')}
          description={t('einrichtung:list.emptyHint')}
          action={
            canWrite ? (
              <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
                {t('einrichtung:actions.create')}
              </Button>
            ) : null
          }
        />
      ) : null}

      {list.data && list.data.length > 0 ? (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('einrichtung:col.name')}</Table.Th>
                  <Table.Th>{t('einrichtung:col.konzept')}</Table.Th>
                  <Table.Th aria-label={t('einrichtung:col.aktionen')} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.map((e) => {
                  const konzept = (e as Einrichtung & { konzept_typ?: string }).konzept_typ ?? '—';
                  return (
                    <Table.Tr key={e.id}>
                      <Table.Td>{e.name}</Table.Td>
                      <Table.Td>{konzept}</Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Tooltip label={t('einrichtung:actions.openPlaene')}>
                            <ActionIcon
                              variant="subtle"
                              onClick={() => {
                                setEinrichtung(e.id);
                                navigate('/plaene');
                              }}
                              aria-label={t('einrichtung:actions.openPlaene')}
                            >
                              <IconCheckupList size={16} />
                            </ActionIcon>
                          </Tooltip>
                          {canWrite ? (
                            <>
                              <Tooltip label={t('einrichtung:actions.edit')}>
                                <ActionIcon
                                  variant="subtle"
                                  onClick={() => setEditTarget(e)}
                                  aria-label={t('einrichtung:actions.edit')}
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t('einrichtung:actions.delete')}>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={() => setDeleteTarget(e)}
                                  aria-label={t('einrichtung:actions.delete')}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </>
                          ) : null}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ) : null}

      <EinrichtungEditDialog
        opened={createOpen}
        traegerId={traegerId}
        einrichtung={null}
        onClose={() => setCreateOpen(false)}
      />
      <EinrichtungEditDialog
        opened={editTarget !== null}
        traegerId={traegerId}
        einrichtung={editTarget}
        onClose={() => setEditTarget(null)}
      />

      {deleteTarget ? (
        <ConfirmDialog
          opened={!!deleteTarget}
          title={t('einrichtung:delete.title')}
          message={t('einrichtung:delete.message', { name: deleteTarget.name })}
          confirmLabel={t('common:actions.delete')}
          danger
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            const target = deleteTarget;
            setDeleteTarget(null);
            try {
              await deleteMutation.mutateAsync(target.id);
              notifications.show({ color: 'teal', message: t('einrichtung:info.deleted') });
            } catch (err) {
              notifications.show({
                color: 'red',
                title: t('errors:generic'),
                message: describeError(err) || t('errors:generic'),
              });
            }
          }}
        />
      ) : null}
    </Stack>
  );
}
